import { Autocomplete, Button, DataTable, DatePicker, useCommonComponentContext } from "@maysoft/common-component-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import Helpers from "commons/helpers";
import Constants from "constants/index";
import Screens from "constants/screens";
import Strings from "constants/strings";
import useDataCommon from "hooks/useDataCommon.hook";
import StatisticsService from "services/medkit/statistics.service";
import BarChartStatistic from "./barChart";
import DataTableByDepartment from "./dataTableByDepartment";
import ModalSummaryNexpectedIncident from "./modalSummaryNexpectedIncident";
import ModalSummaryReportsLevels from "./modalSummaryReportsLevels";
import PieChartStatistic from "./pieChart";

import { ICodename, IRecordClassifyByHospitalStatistics, IRecordDepartmentStatistics } from "commons/interfaces";
import { Tabs } from "components";
import { ITabFilter } from "components/Tabs";
import { BooleanInt } from "constants/enum";
import { DashboardLayout } from "layout";
import { RootState } from "store";
import { setDataAlert, showLoading, showLoadingDataTable } from "store/slice/common.slice";

const colorBarChart = [
    "#F1625E",
    "#9D70FF",
    "#A4C639",
    "#FECD8D",
    "#FFADA6",
    "#104579",
    "#DAF2FF",
    "#24EBC7",
    "#00AFF0",
    "#1C785F",
    "#F7464A",
    "#CAEBF2",
];
const colorTargetChart = ["#F1625E", "#9D70FF", "#A4C639", "#FECD8D", "#FFADA6", "#104579", "#DAF2FF", "#24EBC7", "#00AFF0", "#1C785F", "#F7464A"];
const colorStatusChart = ["#27AE60", "#FEC600", "#F7464A", "#00AFF0", "#1C785F", "#CED4DA"];
const colorTypeChart = ["#CAEBF2", "#F1625E"];

enum ButtonTab {
    OPENING = 1,
    BY_WEEK = 2,
    BY_MONTH = 3,
    BY_YEAR = 4,
    BY_DEPARTMENT = 5,
    BY_HOSPITAL = 6,
    INCIDENT_TRACKING = 7,
}

type IRangeDateTime = {
    endTime?: string | number;
    startTime?: string | number;
};
const FILTER_TABS: (ITabFilter & {
    rangeDateTime?: IRangeDateTime;
})[] = [
    {
        value: ButtonTab.OPENING,
        title: Strings.CHART.OPENING,
    },
    {
        value: ButtonTab.BY_WEEK,
        title: Strings.CHART.BY_WEEK,
        rangeDateTime: {
            startTime: moment().startOf("week").unix(),
            endTime: moment().endOf("week").unix(),
        },
    },
    {
        value: ButtonTab.BY_MONTH,
        title: Strings.CHART.BY_MONTH,
        rangeDateTime: {
            startTime: moment().startOf("month").unix(),
            endTime: moment().endOf("month").unix(),
        },
    },
    {
        value: ButtonTab.BY_YEAR,
        title: Strings.CHART.BY_YEAR,
        rangeDateTime: {
            startTime: moment().startOf("year").unix(),
            endTime: moment().endOf("year").unix(),
        },
    },
    {
        value: ButtonTab.BY_DEPARTMENT,
        title: Strings.CHART.BY_DEPARTMENT,
    },
    {
        value: ButtonTab.BY_HOSPITAL,
        title: Strings.CHART.BY_HOSPITAL,
    },
    {
        value: ButtonTab.INCIDENT_TRACKING,
        title: Strings.CHART.INCIDENT_TRACKING,
    },
];

export interface IDataSeries {
    value: number;
    color: string;
    textLabel: string;
}

interface IDataFilter {
    tabIndex: ButtonTab;
    dateTime?: any;
    groupId?: string;
    pageSize?: number;
    pageNumber?: number;
    numberYear?: number;
    rangeDateTime?: IRangeDateTime;
}

const statisticsService = new StatisticsService();

const StatisticScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const { getAllDepartments } = useDataCommon();
    const { clientId, userInfo } = useCommonComponentContext();

    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);

    const [dataFilter, setDataFilter] = useState<IDataFilter>({ tabIndex: ButtonTab.OPENING });

    const [openModal1, setOpenModal1] = useState<boolean>(false);
    const [openModal2, setOpenModal2] = useState<boolean>(false);

    const [totalReports, setTotalReports] = useState<number>(0);
    const [totalReportType, setTotalReportType] = useState<number>(0);
    const [totalReportTarget, setTotalReportTarget] = useState<number>(0);
    const [totalReportStatus, setTotalReportStatus] = useState<number>(0);

    const [dataTypeSeries, setDataTypeSeries] = useState<IDataSeries[]>([]);
    const [dataTargetSeries, setDataTargetSeries] = useState<IDataSeries[]>([]);
    const [dataStatusSeries, setDataStatusSeries] = useState<IDataSeries[]>([]);
    const [dataReportSeries, setDataReportSeries] = useState<IDataSeries[]>([]);

    const [totalCountyHospital, setTotalCountyHospital] = useState<number>(0);
    const [totalCountByDepartment, setTotalCountByDepartment] = useState<number>(0);
    const [dataRowsByDepartment, setDataRowsByDepartment] = useState<IRecordDepartmentStatistics[]>([]);
    const [dataRowsByHospital, setDataRowsByHospital] = useState<IRecordClassifyByHospitalStatistics[]>([]);

    const [dataGroups, setDataGroups] = useState<ICodename[]>([]);

    const tab = searchParams.get("tab");
    const to = searchParams.get("to");
    const from = searchParams.get("from");
    const date = searchParams.get("date");
    const size = searchParams.get("size");
    const page = searchParams.get("page");
    const numberYear = searchParams.get("year");
    const department = searchParams.get("department");

    useEffect(() => {
        (async () => {
            try {
                const result = await getAllDepartments({
                    clientId: clientId,
                    organizationId: userInfo?.userProfile?.organizationId,
                });
                setDataGroups(result);
            } catch (error) {}
        })();
    }, []);

    useEffect(() => {
        onStart({
            groupId: department,
            dateTime: date ? Number(date) : undefined,
            tabIndex: tab ? Number(tab) : ButtonTab.OPENING,
            pageSize: size ? Number(size) : Constants.ROW_PER_PAGE,
            pageNumber: page ? Number(page) : Constants.PAGE_DEFAULT,
            numberYear: numberYear ? Number(numberYear) : undefined,
            rangeDateTime: {
                endTime: to ? Number(to) : undefined,
                startTime: from ? Number(from) : undefined,
            },
        });
    }, [tab, to, from, date, page, size, numberYear, department]);

    const onStart = async (data: IDataFilter) => {
        setDataFilter(data);

        if ([ButtonTab.OPENING, ButtonTab.BY_WEEK, ButtonTab.BY_MONTH, ButtonTab.BY_YEAR].includes(data.tabIndex)) {
            let req: {
                endTime?: string | number;
                startTime?: string | number;
                onlyOpenedReport?: number;
            };
            if (data.tabIndex === ButtonTab.OPENING) {
                req = { onlyOpenedReport: BooleanInt.True };
            } else {
                if(Helpers.isNullOrEmpty(data?.rangeDateTime?.endTime)){
                    req = FILTER_TABS[data.tabIndex - 1]?.rangeDateTime;
                }else{
                    req = data.rangeDateTime;
                }
            }
            await getStatistics(req);
            return;
        }

        if (data.tabIndex === ButtonTab.BY_DEPARTMENT) {
            await getDepartmentStatistics({
                groupId: data.groupId,
                dateTime: data.dateTime,
                pageSize: data.pageSize,
                pageNumber: data.pageNumber,
            });
            return;
        }

        if (data.tabIndex === ButtonTab.BY_HOSPITAL) {
            await getClassifyByHospitalStatistics({
                pageSize: data.pageSize,
                pageNumber: data.pageNumber,
            });
            return;
        }

        if (data.tabIndex === ButtonTab.INCIDENT_TRACKING) {
            await getStatisticsByYear({
                year: data.numberYear,
                groupId: data.groupId,
            });
            return;
        }
    };

    const getStatistics = async (req: { endTime?: string | number; startTime?: string | number; onlyOpenedReport?: number }) => {
        try {
            dispatch(showLoading(true));

            const result = await statisticsService.getStatistics(req);

            let newTotalType = 0;
            const newDataTypeSeries: IDataSeries[] = [];
            Object.values(result.reportType).forEach((element: any, index) => {
                newTotalType = newTotalType + element.value || 0;
                newDataTypeSeries.push({
                    value: element.value || 0,
                    color: colorTypeChart[index],
                    textLabel: element.label,
                });
            });

            let newTotalTarget = 0;
            const newDataTargetSeries: IDataSeries[] = [];
            Object.values(result.incidentObject).forEach((element: any, index) => {
                newTotalTarget = newTotalTarget + element.value || 0;
                newDataTargetSeries.push({
                    value: element.value || 0,
                    color: colorTargetChart[index],
                    textLabel: element.label,
                });
            });

            let newTotalStatus = 0;
            const newDataStatusSeries: IDataSeries[] = [];
            Object.values(result.reportStatus).forEach((element: any, index) => {
                newTotalStatus = newTotalStatus + element.value || 0;
                newDataStatusSeries.push({
                    value: element.value || 0,
                    color: colorStatusChart[index],
                    textLabel: element.label,
                });
            });

            setTotalReportType(newTotalType);
            setDataTypeSeries(newDataTypeSeries);

            setTotalReportTarget(newTotalTarget);
            setDataTargetSeries(newDataTargetSeries);

            setTotalReportStatus(newTotalStatus);
            setDataStatusSeries(newDataStatusSeries);
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoading(false));
        }
    };

    const getDepartmentStatistics = async (req: { groupId?: string; dateTime: any; pageSize: number; pageNumber: number }) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await statisticsService.getDepartmentStatistics({
                date: req.dateTime,
                groupIds: req.groupId ? [req.groupId] : [],
                pageSize: req.pageSize,
                pageNumber: req.pageNumber,
            });

            setTotalCountByDepartment(result?.totalCount || 0);
            setDataRowsByDepartment([...(result?.items || [])]);
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const getClassifyByHospitalStatistics = async (req: { pageSize?: number; pageNumber?: number }) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await statisticsService.getClassifyByHospitalStatistics(req);

            setTotalCountyHospital(result?.totalCount || 0);
            setDataRowsByHospital([...(result?.items || [])]);
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const getStatisticsByYear = async (req: { year: number; groupId?: string }) => {
        try {
            dispatch(showLoadingDataTable(true));

            const result = await statisticsService.getStatisticsByYear(req);

            let newTotal = 0;
            const newDataSeries: IDataSeries[] = [];
            Object.values(result).forEach((element: any, index) => {
                newTotal = newTotal + Number(element.value || 0);
                newDataSeries.push({
                    value: Number(element.value || 0),
                    color: colorBarChart[index],
                    textLabel: `${Strings.DATE_RANGE_PICKER.MONTH} ${element.label}`,
                });
            });

            setTotalReports(newTotal);
            setDataReportSeries(newDataSeries);
        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleChangeValue = (newData: IDataFilter) => {
        let newDateTime = newData.dateTime;
        let newPageSize = newData.pageSize;
        let newPageNumber = newData.pageNumber;
        let newNumberYear = newData.numberYear;

        if (Helpers.isNullOrEmpty(newData.dateTime) && newData.tabIndex === ButtonTab.BY_DEPARTMENT) {
            newDateTime = moment().unix();
        }
        if (Helpers.isNullOrEmpty(newData.pageNumber) && [ButtonTab.BY_DEPARTMENT, ButtonTab.BY_HOSPITAL].includes(newData.tabIndex)) {
            newPageNumber = Constants.PAGE_DEFAULT;
        }
        if (Helpers.isNullOrEmpty(newData.pageSize) && [ButtonTab.BY_DEPARTMENT, ButtonTab.BY_HOSPITAL].includes(newData.tabIndex)) {
            newPageSize = Constants.ROW_PER_PAGE;
        }

        if (Helpers.isNullOrEmpty(newData.numberYear) && newData.tabIndex === ButtonTab.INCIDENT_TRACKING) {
            newNumberYear = moment().year();
        }

        let query: string = `?tab=${newData.tabIndex || ButtonTab.OPENING}`;

        if (!Helpers.isNullOrEmpty(newData.rangeDateTime?.startTime)) {
            query += `&from=${newData.rangeDateTime?.startTime}`;
        }
        if (!Helpers.isNullOrEmpty(newData.rangeDateTime?.endTime)) {
            query += `&to=${newData.rangeDateTime?.endTime}`;
        }

        if (!Helpers.isNullOrEmpty(newDateTime)) {
            query += `&date=${newDateTime}`;
        }

        if (!Helpers.isNullOrEmpty(newNumberYear)) {
            query += `&year=${newNumberYear}`;
        }

        if (!Helpers.isNullOrEmpty(newData.groupId)) {
            query += `&groupId=${newData.groupId}`;
        }

        if (!Helpers.isNullOrEmpty(newPageNumber)) {
            query += `&page=${newPageNumber}`;
        }

        if (!Helpers.isNullOrEmpty(newPageSize)) {
            query += `&size=${newPageSize}`;
        }

        navigate(Screens.STATISTIC + query);
    };

    const rowsByHospital = useMemo(() => [...(dataRowsByHospital || [])], [dataRowsByHospital]);
    const columnsByHospital = useMemo(
        () => [
            { Header: Strings.REPORT.HOSPITAL_CLASS, accessor: "name", width: "250px" },
            { Header: Strings.REPORT.STATUS.NEW, accessor: "statusNew", width: "80px", align: "center" },
            { Header: Strings.REPORT.STATUS.ANALYZED, accessor: "analyzed", width: "80px", align: "center" },
            { Header: Strings.REPORT.STATUS.PROCESSING, accessor: "processing", width: "80px", align: "center" },
            { Header: Strings.REPORT.STATUS.PROCESSED, accessor: "processed", width: "80px", align: "center" },
            { Header: Strings.REPORT.STATUS.CANCELLED, accessor: "cancelled", width: "80px", align: "center" },
            { Header: Strings.REPORT.STATUS.CLOSED, accessor: "closed", width: "80px", align: "center" },
        ],
        [],
    );

    const rowsByDepartment = useMemo(() => [...(dataRowsByDepartment || [])], [dataRowsByDepartment]);

    return (
        <DashboardLayout isPermission requiredOrganization title={Strings.CHART.STATISTICS} route={[{ title: Strings.CHART.STATISTICS, route: "" }]}>
            <Grid container spacing={3}>
                {/* Btn Export */}
                <Grid item xs={12} sm={12} md={12}>
                    <Box
                        sx={{
                            gap: 1,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}>
                        <Button
                            color="info"
                            onClick={() => {
                                setOpenModal1(true);
                            }}>
                            {Strings.CHART.SUMMARY_REPORTS_LEVELS}
                        </Button>
                        <Button
                            color="info"
                            onClick={() => {
                                setOpenModal2(true);
                            }}>
                            {Strings.CHART.SUMMARY_UNEXPECTED_INCIDENT}
                        </Button>
                    </Box>
                </Grid>

                {/* Input Filter */}
                {![ButtonTab.OPENING, ButtonTab.BY_HOSPITAL].includes(dataFilter.tabIndex) && (
                    <Grid item xs={12} sm={12} md={12}>
                        <Box
                            sx={{
                                gap: 1,
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifyContent: "end",
                            }}>
                            {dataFilter.tabIndex === ButtonTab.BY_WEEK && (
                                <Box
                                    sx={{
                                        gap: 1,
                                        display: "flex",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}>
                                    <Box width={"250px"}>
                                        <DatePicker
                                            variant={"outlined"}
                                            value={Helpers.toUnix(dataFilter.rangeDateTime?.startTime)}
                                            onChangeValue={(value: any) => {
                                                const valueEndTime = !Helpers.isNullOrEmpty(value) ? moment(value).endOf("week").unix() : undefined;
                                                const valueStartTime = !Helpers.isNullOrEmpty(value)
                                                    ? moment(value).startOf("week").unix()
                                                    : undefined;
                                                handleChangeValue({
                                                    ...dataFilter,
                                                    rangeDateTime: {
                                                        endTime: valueEndTime,
                                                        startTime: valueStartTime,
                                                    },
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {dataFilter.tabIndex === ButtonTab.BY_MONTH && (
                                <Box
                                    sx={{
                                        gap: 1,
                                        display: "flex",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}>
                                    <Box width={"250px"}>
                                        <DatePicker
                                            variant={"outlined"}
                                            views={["month", "year"]}
                                            value={Helpers.toUnix(dataFilter.rangeDateTime?.startTime)}
                                            onChangeValue={(value: any) => {
                                                const valueEndTime = !Helpers.isNullOrEmpty(value) ? moment(value).endOf("month").unix() : undefined;
                                                const valueStartTime = !Helpers.isNullOrEmpty(value)
                                                    ? moment(value).startOf("month").unix()
                                                    : undefined;
                                                handleChangeValue({
                                                    ...dataFilter,
                                                    rangeDateTime: {
                                                        endTime: valueEndTime,
                                                        startTime: valueStartTime,
                                                    },
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {dataFilter.tabIndex === ButtonTab.BY_YEAR && (
                                <Box
                                    sx={{
                                        gap: 1,
                                        display: "flex",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}>
                                    <Box width={"250px"}>
                                        <DatePicker
                                            views={["year"]}
                                            variant={"outlined"}
                                            value={Helpers.toUnix(dataFilter.rangeDateTime?.startTime)}
                                            onChangeValue={(value: any) => {
                                                const valueEndTime = !Helpers.isNullOrEmpty(value) ? moment(value).endOf("year").unix() : undefined;
                                                const valueStartTime = !Helpers.isNullOrEmpty(value)
                                                    ? moment(value).startOf("year").unix()
                                                    : undefined;
                                                handleChangeValue({
                                                    ...dataFilter,
                                                    rangeDateTime: {
                                                        endTime: valueEndTime,
                                                        startTime: valueStartTime,
                                                    },
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {dataFilter.tabIndex === ButtonTab.INCIDENT_TRACKING && (
                                <Box
                                    sx={{
                                        gap: 1,
                                        display: "flex",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}>
                                    <Box width={"250px"}>
                                        <DatePicker
                                            views={["year"]}
                                            variant={"outlined"}
                                            value={Helpers.toUnix(moment(`${dataFilter.numberYear}-01-01`, "YYYY-MM-DD").unix())}
                                            onChangeValue={(value: any) => {
                                                const newValue = !Helpers.isNullOrEmpty(value) ? moment(value).year() : undefined;
                                                handleChangeValue({
                                                    ...dataFilter,
                                                    numberYear: newValue,
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {dataFilter.tabIndex === ButtonTab.BY_DEPARTMENT && (
                                <Box
                                    sx={{
                                        gap: 1,
                                        display: "flex",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}>
                                    <Box width={"250px"}>
                                        <DatePicker
                                            variant={"outlined"}
                                            value={Helpers.toUnix(dataFilter.dateTime)}
                                            onChangeValue={(value: any) => {
                                                const newValue = !Helpers.isNullOrEmpty(value) ? moment(value).unix() : undefined;
                                                handleChangeValue({
                                                    ...dataFilter,
                                                    dateTime: newValue,
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                            {[ButtonTab.BY_DEPARTMENT, ButtonTab.INCIDENT_TRACKING].includes(dataFilter.tabIndex) && (
                                <Box
                                    sx={{
                                        gap: 1,
                                        display: "flex",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}>
                                    <Box width={"250px"}>
                                        <Autocomplete
                                            data={dataGroups}
                                            variant={"outlined"}
                                            defaultValue={dataFilter.groupId}
                                            placeholder={Strings.REPORT.SELECT_DEPARTMENT}
                                            onChange={(value: any) => {
                                                handleChangeValue({
                                                    ...dataFilter,
                                                    groupId: value,
                                                });
                                            }}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                )}

                {/* Tabbar */}
                <Grid item xs={12}>
                    <Card>
                        <Box
                            sx={{
                                gap: 1,
                                padding: 2,
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifyContent: "center",
                            }}>
                            <Tabs
                                tabs={FILTER_TABS}
                                value={dataFilter.tabIndex}
                                onChangeTab={(item) => {
                                    handleChangeValue({ tabIndex: item.value });
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>

                {[ButtonTab.OPENING, ButtonTab.BY_WEEK, ButtonTab.BY_MONTH, ButtonTab.BY_YEAR].includes(dataFilter.tabIndex) && (
                    <>
                        <Grid item xs={12}>
                            <Card>
                                <PieChartStatistic
                                    key={"TARGET_OBJECT"}
                                    total={totalReportTarget}
                                    dataSeries={dataTargetSeries}
                                    title={Strings.CHART.TARGET_OBJECT}
                                />
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            <Card>
                                <PieChartStatistic
                                    key={"INCIDENT_STATUS"}
                                    total={totalReportStatus}
                                    dataSeries={dataStatusSeries}
                                    title={Strings.CHART.INCIDENT_STATUS}
                                />
                            </Card>
                        </Grid>
                        <Grid item xs={12}>
                            <Card>
                                <PieChartStatistic
                                    key={"INCIDENT_TYPE"}
                                    total={totalReportType}
                                    dataSeries={dataTypeSeries}
                                    title={Strings.CHART.INCIDENT_TYPE}
                                />
                            </Card>
                        </Grid>
                    </>
                )}

                {dataFilter.tabIndex === ButtonTab.BY_DEPARTMENT && (
                    <Grid item xs={12}>
                        <Card sx={{ padding: 2 }}>
                            <DataTableByDepartment
                                data={rowsByDepartment}
                                loadingData={loadingDataTable}
                                totalCount={totalCountByDepartment}
                                pageSize={dataFilter.pageSize || Constants.ROW_PER_PAGE}
                                pageNumber={dataFilter.pageNumber || Constants.PAGE_DEFAULT}
                                onChangePageNumber={(value) => {
                                    handleChangeValue({
                                        ...dataFilter,
                                        pageNumber: value,
                                    });
                                }}
                                onChangePageSize={(value) => {
                                    handleChangeValue({
                                        ...dataFilter,
                                        pageSize: value,
                                    });
                                }}
                            />
                        </Card>
                    </Grid>
                )}

                {dataFilter.tabIndex === ButtonTab.BY_HOSPITAL && (
                    <Grid item xs={12}>
                        <Card sx={{ padding: 2 }}>
                            <DataTable
                                isShowIndex
                                loading={loadingDataTable}
                                totalCount={totalCountyHospital}
                                rowPerPage={dataFilter.pageSize || Constants.ROW_PER_PAGE}
                                pageNumber={dataFilter.pageNumber || Constants.PAGE_DEFAULT}
                                onChangePageSize={(value) => {
                                    handleChangeValue({
                                        ...dataFilter,
                                        pageSize: value,
                                    });
                                }}
                                onChangePageNumber={(value) => {
                                    handleChangeValue({
                                        ...dataFilter,
                                        pageNumber: value,
                                    });
                                }}
                                table={{ columns: columnsByHospital as any, rows: rowsByHospital }}
                            />
                        </Card>
                    </Grid>
                )}

                {dataFilter.tabIndex === ButtonTab.INCIDENT_TRACKING && (
                    <Grid item xs={12}>
                        <Card>
                            <BarChartStatistic key={"INCIDENT_TRACKING"} total={totalReports} dataSeries={dataReportSeries} />
                        </Card>
                    </Grid>
                )}
            </Grid>

            {openModal1 && <ModalSummaryReportsLevels open={openModal1} setOpen={setOpenModal1} />}
            {openModal2 && <ModalSummaryNexpectedIncident open={openModal2} setOpen={setOpenModal2} />}
        </DashboardLayout>
    );
};

export default StatisticScreen;
